import React, { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { LoginPage } from './components/Login/LoginPage';
import VerificationCode from './components/VerificationCode/VerificationCode';
import AuthLayout from './components/Layouts/AuthLayout';
import MainLayout from './components/Layouts/MainLayout';
import ProtectedRoute from './components/ProtectedRoute';
import { CompanyProviderFromUrl } from './components/CompanyProviderFromUrl';
import RouteWrapper from './components/RouteWrapper';
import LazyRouteWrapper from './components/LazyRouteWrapper';
import RouteLoadingSpinner from './components/Common/RouteLoadingSpinner';
import ResetPassword from './components/ResetPassword/ResetPassword';
import ForgotPassword from './components/ForgotPassword/ForgotPassword';
import CompleteAccount from './components/Auth/CompleteAccount';
import UnauthorizedPage from './components/UnauthorizedPage/UnauthorizedPage';
import AdminRouteGuard from './components/AdminRouteGuard';
import StaffOnlyRoute from './components/StaffOnlyRoute';
import DealerFlagRoute from './components/DealerFlagRoute';
import ReportsIndexRedirect from './components/Reports/ReportsIndexRedirect';

// Lazy-loaded components
import {
  AdminBlackBook,
  AdminBugReports,
  AdminReportTemplates,
  AdminImportsMonitor,
  AdminDashboard,
  AdminDatabaseInfo,
  AdminDCSApi,
  AdminAIAssistants,
  AdminGeolocation,
  AdminNewUser,
  AdminLogs,
  AdminFTPStatus,
  AdminTestFileGenerator,
  AdminDocuSign,
  AdminPowerBI,
  AdminLoansToLeases,
  AdminARaging,
  EditUser,
  AdminUsers,
  AdminVideos,
  AdminCharts,
  AdminDocLibrary,
  AdminBuyerNames,
  AdminLpEstBillingDetail,
  AdminLpDailyLog,
  // AdminLoginLog, // standalone Auth Log page disabled — the log now lives on the
  // Users page (AdminUsers.tsx) + a Dashboard widget. Restore this import and the
  // /admin/login-log route below (and the routesConfig menu entry) to re-enable it.
  ReleaseNotes,
  AIChat,
  BorrowingBase,
  BorrowingBaseBilling,
  BorrowingBaseBillingLogs,
  BorrowingBaseCurrentAR,
  BorrowingBaseDashboard,
  BorrowingBaseDocuSignDocs,
  BorrowingBaseTransactions,
  Dealers,
  DealerDetail,
  DealerBillingOptions,
  DealerContacts,
  DealerNewContact,
  DealerEditContact,
  DealerCorporateDocs,
  DealerBorrowingBase,
  DealerDefaults,
  DealerDMSGPSInfo,
  DealerMap,
  DealerPortalUsers,
  DealerNewPortalUser,
  DealerEditPortalUser,
  DealerRisk,
  DealerUnitProjections,
  DealerAdd,
  FloorplansSummaryByDealer,
  FloorplansDashboard,
  FloorplansPendingFunding,
  FloorplanAddVehicle,
  FloorplanAddVendor,
  FloorplanVendors,
  FloorplansFunding,
  FloorplansCancelFunding,
  FloorplansActiveVehicles,
  FloorplansActiveVehiclesDetail,
  FloorplanBilling,
  FloorplansPayoffRequests,
  FloorplansBulkPayoff,
  FloorplansPaidOffVehicles,
  FloorplansFundingLog,
  FloorplansFundingLogDetail,
  FloorplansBillingPaymentLog,
  FloorplansBillingPaymentLogDetail,
  FloorplansAssignLender,
  FloorplansBalanceHistory,
  HomeBorrowingBaseReports,
  HomeBulkLoanPlanBilling,
  HomeDashboards,
  HomeDocuSignStatus,
  HomeExports,
  HomeExportsDetail,
  HomeNewsInfo,
  HomeNewsInfoAddNew,
  HomePrimeRateHistory,
  HomeLeaseQuote,
  HomeVinSearch,
  // HomeDirectory, // Phone Directory disabled v0.63.8 — phoneext table is empty.
  HomeCalendar,
  ImportsSummary,
  ImportsCheckForDuplicates,
  ImportsCurrentDealerAR,
  ImportsARAged,
  ImportsARBalance,
  ImportsAccDepreciation,
  ImportsBHPHLoanRequests,
  ImportsInterestBalance,
  ImportsFloorplanRequest,
  ImportsIndirectLoanRequests,
  ImportsLoanRequests,
  ImportsPremierLoanRequests,
  ImportsRefiSwapPremierConversion,
  ImportsUnsoldInventory,
  ImportsVinToStockNumXref,
  ImportsWholesaleInventory,
  LoansHome,
  LoansBilling,
  LoansActiveLoans,
  LoansActiveLoansDetail,
  LoansRepoSwap,
  LoansBillingPaymentLog,
  LoansBillingPaymentLogDetail,
  LoansCloseFullTermLoans,
  LoanDashboard,
  LoansDocuSignStatus,
  LoansExceptionsMaintenance,
  LoansFundingLog,
  LoansFundingLogDetail,
  LoansLoanCalculator,
  LoansLoanPlanPayoffRequests,
  LoansLoanPlanBulkPayoff,
  LoansBulkPayoffRequests,
  LoansPayoffRequestsList,
  LoansCancelSwapRequest,
  LoansAddLoan,
  LoansFundLoans,
  LoansCancelLoanRequests,
  MatchupExceptions,
  MatchupExceptionDetail,
  LoansLoanMatchStatus,
  LoansMoveLoansToFloorplan,
  LoansPaidOffLoans,
  LoansPaidOffCalculator,
  LoansPendingFunding,
  LoansPendingQueue,
  LoansPendingRepoSwaps,
  LoansRepoSwapApproval,
  LoansTrancheExceptions,
  LoanToLeaseMatchup,
  ReportsAllReports,
  DealerReports,
  ReportsCreditLines,
  ReportsEmailReports,
  ReportsFloorplans,
  SettingsStaffUsers,
  SettingsSignatures,
  SettingsEmailTemplates,
  SettingsRoboTemplates,
  SettingsLetterTemplates,
  SettingsTextTemplates,
  UserProfile,
  VideoTraining,
} from './components/LazyComponents';

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public routes with AuthLayout */}
      <Route element={<AuthLayout />}>
        <Route path="/sign-in" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/verify" element={<VerificationCode />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/complete-account" element={<CompleteAccount />} />
      </Route>

      {/* Protected routes with MainLayout */}
      <Route
        element={
          <ProtectedRoute>
            <CompanyProviderFromUrl>
              <MainLayout />
            </CompanyProviderFromUrl>
          </ProtectedRoute>
        }
      >
        <Route
          path="/admin"
          element={
            <LazyRouteWrapper>
              <AdminDashboard />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/admin/dcs-api"
          element={
            // requireOrgAdmin alone admits a dealer admin; with requireLHPHAccess it is
            // SuperAdmin and Admin only. The API refuses every dealer login (8du3).
            <AdminRouteGuard requireOrgAdmin requireLHPHAccess>
              <LazyRouteWrapper>
                <AdminDCSApi />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/ai-assistants"
          element={
            // requireOrgAdmin alone admits a dealer admin; the chat API refuses every dealer.
            <AdminRouteGuard requireOrgAdmin requireLHPHAccess>
              <LazyRouteWrapper>
                <AdminAIAssistants enableTerryChat={true} />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/dashboard"
          element={
            <AdminRouteGuard requireLHPHAdmin>
              <LazyRouteWrapper>
                <AdminDashboard />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/users/new"
          element={
            <AdminRouteGuard requireUserCreation={true}>
              <LazyRouteWrapper>
                <AdminNewUser />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/users/:userId"
          element={
            <AdminRouteGuard isUsersRoute>
              <LazyRouteWrapper>
                <EditUser />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/users"
          element={
            <AdminRouteGuard isUsersRoute={true}>
              <LazyRouteWrapper>
                <AdminUsers />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/geolocation"
          element={
            <AdminRouteGuard requireLHPHAdmin>
              <LazyRouteWrapper>
                <AdminGeolocation />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/release-notes"
          element={
            <AdminRouteGuard isUsersRoute>
              <LazyRouteWrapper>
                <ReleaseNotes />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/videos"
          element={
            <AdminRouteGuard requireLHPHAdmin>
              <LazyRouteWrapper>
                <AdminVideos />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/charts"
          element={
            <AdminRouteGuard requireLHPHAdmin>
              <LazyRouteWrapper>
                <AdminCharts />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/doc-library"
          element={
            <AdminRouteGuard requireLHPHAdmin>
              <LazyRouteWrapper>
                <AdminDocLibrary />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/buyer-names"
          element={
            <AdminRouteGuard requireLHPHAdmin>
              <LazyRouteWrapper>
                <AdminBuyerNames />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/lp-est-billing-detail"
          element={
            <AdminRouteGuard requireLHPHAdmin>
              <LazyRouteWrapper>
                <AdminLpEstBillingDetail />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/lp-daily-log"
          element={
            <AdminRouteGuard requireLHPHAdmin>
              <LazyRouteWrapper>
                <AdminLpDailyLog />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        {/* Standalone Auth Log page disabled — the App/Legacy auth-log tabs now
            render on the Users page (AdminUsers.tsx) and a 10-row widget on the
            Dashboard. Un-comment this route + the AdminLoginLog import above + the
            routesConfig menu entry to restore the standalone page.
        <Route
          path="/admin/login-log"
          element={
            <AdminRouteGuard requireLHPHAdmin>
              <LazyRouteWrapper>
                <AdminLoginLog />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        */}

        {/* Settings Routes — Letter + Email Templates wired to backend; the rest still need backend wiring */}
        <Route
          path="/admin/settings/letter-templates"
          element={
            <AdminRouteGuard requireLHPHAdmin>
              <LazyRouteWrapper>
                <SettingsLetterTemplates />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/settings/email-templates"
          element={
            <AdminRouteGuard requireLHPHAdmin>
              <LazyRouteWrapper>
                <SettingsEmailTemplates />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        {/*
        <Route path="/admin/settings/staff-users" element={<AdminRouteGuard requireLHPHAdmin><LazyRouteWrapper><SettingsStaffUsers /></LazyRouteWrapper></AdminRouteGuard>} />
        <Route path="/admin/settings/signatures" element={<AdminRouteGuard requireLHPHAdmin><LazyRouteWrapper><SettingsSignatures /></LazyRouteWrapper></AdminRouteGuard>} />
        <Route path="/admin/settings/robo-templates" element={<AdminRouteGuard requireLHPHAdmin><LazyRouteWrapper><SettingsRoboTemplates /></LazyRouteWrapper></AdminRouteGuard>} />
        <Route path="/admin/settings/text-templates" element={<AdminRouteGuard requireLHPHAdmin><LazyRouteWrapper><SettingsTextTemplates /></LazyRouteWrapper></AdminRouteGuard>} />
        */}
        <Route
          path="/admin/logs"
          element={
            <AdminRouteGuard requireLHPHAdmin={true}>
              <LazyRouteWrapper>
                <AdminLogs />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/bug-reports"
          element={
            <AdminRouteGuard requireSuperAdmin={true}>
              <LazyRouteWrapper>
                <AdminBugReports />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/ftp-status"
          element={
            <AdminRouteGuard requireLHPHAdmin={true}>
              <LazyRouteWrapper>
                <AdminFTPStatus />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/test-file-generator"
          element={
            <AdminRouteGuard requireLHPHAdmin={true}>
              <LazyRouteWrapper>
                <AdminTestFileGenerator />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/database-info"
          element={
            <AdminRouteGuard requireLHPHAdmin>
              <LazyRouteWrapper>
                <AdminDatabaseInfo />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/docusign"
          element={
            <AdminRouteGuard requireLHPHAdmin>
              <LazyRouteWrapper>
                <AdminDocuSign />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/blackbook"
          element={
            <AdminRouteGuard requireLHPHAdmin>
              <LazyRouteWrapper>
                <AdminBlackBook />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/report-templates"
          element={
            <AdminRouteGuard requireLHPHAdmin>
              <LazyRouteWrapper>
                <AdminReportTemplates />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/imports-monitor"
          element={
            <AdminRouteGuard requireLHPHAdmin>
              <LazyRouteWrapper>
                <AdminImportsMonitor />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/daily-kpis"
          element={
            <AdminRouteGuard requireLHPHAccess={true}>
              <LazyRouteWrapper>
                <AdminPowerBI />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/loans-to-leases"
          element={
            <AdminRouteGuard requireLHPHAccess={true}>
              <LazyRouteWrapper>
                <AdminLoansToLeases />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />
        <Route
          path="/admin/ar-aging"
          element={
            <AdminRouteGuard requireLHPHAccess={true}>
              <LazyRouteWrapper>
                <AdminARaging />
              </LazyRouteWrapper>
            </AdminRouteGuard>
          }
        />

        <Route
          path="/ai-chat"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <AIChat />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />

        <Route
          path="/borrowing-base"
          element={
            <LazyRouteWrapper>
              <BorrowingBase />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/borrowing-base/:id/billing"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <BorrowingBaseBilling />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/borrowing-base/:id/billing-logs"
          element={
            <DealerFlagRoute flags={['BorrowBase']}>
              <LazyRouteWrapper>
                <BorrowingBaseBillingLogs />
              </LazyRouteWrapper>
            </DealerFlagRoute>
          }
        />
        <Route
          path="/borrowing-base/:id/current-ar"
          element={
            <DealerFlagRoute flags={['BorrowBase']}>
              <LazyRouteWrapper>
                <BorrowingBaseCurrentAR />
              </LazyRouteWrapper>
            </DealerFlagRoute>
          }
        />
        <Route
          path="/borrowing-base/:id/dashboard"
          element={
            <DealerFlagRoute flags={['BorrowBase']}>
              <LazyRouteWrapper>
                <BorrowingBaseDashboard />
              </LazyRouteWrapper>
            </DealerFlagRoute>
          }
        />
        <Route
          path="/borrowing-base/:id/docusign-docs"
          element={
            <DealerFlagRoute flags={['BorrowBase']}>
              <LazyRouteWrapper>
                <BorrowingBaseDocuSignDocs />
              </LazyRouteWrapper>
            </DealerFlagRoute>
          }
        />
        <Route
          path="/borrowing-base/:id/transactions"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <BorrowingBaseTransactions />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />

        <Route
          path="/dealers"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <Dealers data-test="dealers" />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/dealers/new"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <DealerAdd />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/dealers/:id"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <DealerDetail />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/dealers/:id/billing-options"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <DealerBillingOptions />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/dealers/:id/contacts"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <DealerContacts />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/dealers/:id/contacts/new"
          element={
            <StaffOnlyRoute>
              <AdminRouteGuard requireContactCreation={true}>
                <LazyRouteWrapper>
                  <DealerNewContact />
                </LazyRouteWrapper>
              </AdminRouteGuard>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/dealers/:id/contacts/:userId/edit"
          element={
            <StaffOnlyRoute>
              <AdminRouteGuard requireContactCreation={true}>
                <LazyRouteWrapper>
                  <DealerEditContact />
                </LazyRouteWrapper>
              </AdminRouteGuard>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/dealers/:id/corporate-docs"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <DealerCorporateDocs />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/dealers/:id/dealer-borrowing-base"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <DealerBorrowingBase />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/dealers/:id/defaults"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <DealerDefaults />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/dealers/:id/details"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <DealerDetail />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/dealers/:id/dms-gps-info"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <DealerDMSGPSInfo />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/dealers/:id/map"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <DealerMap />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/dealers/:id/portal-users"
          element={
            <StaffOnlyRoute>
              <AdminRouteGuard requireOrgAdmin={true}>
                <LazyRouteWrapper>
                  <DealerPortalUsers />
                </LazyRouteWrapper>
              </AdminRouteGuard>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/dealers/:id/portal-users/new"
          element={
            <StaffOnlyRoute>
              <AdminRouteGuard requireOrgAdmin={true}>
                <LazyRouteWrapper>
                  <DealerNewPortalUser />
                </LazyRouteWrapper>
              </AdminRouteGuard>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/dealers/:id/portal-users/:userId/edit"
          element={
            <StaffOnlyRoute>
              <AdminRouteGuard requireOrgAdmin={true}>
                <LazyRouteWrapper>
                  <DealerEditPortalUser />
                </LazyRouteWrapper>
              </AdminRouteGuard>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/dealers/:id/risk"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <DealerRisk />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/dealers/:id/unit-projections"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <DealerUnitProjections />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />

        <Route
          path="/floorplans"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <FloorplansSummaryByDealer />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/floorplans/:id/dashboard"
          element={
            <DealerFlagRoute flags={['FloorplanON']}>
              <LazyRouteWrapper>
                <FloorplansDashboard />
              </LazyRouteWrapper>
            </DealerFlagRoute>
          }
        />
        <Route
          path="/floorplans/:id/pending-funding"
          element={
            <DealerFlagRoute flags={['FloorplanON']}>
              <LazyRouteWrapper>
                <FloorplansPendingFunding />
              </LazyRouteWrapper>
            </DealerFlagRoute>
          }
        />
        {/* <Route
          path="/floorplans/:id/pending-funding/detail/:floorplanId"
          element={
            <LazyRouteWrapper>
              <FloorplansActiveVehiclesDetail />
            </LazyRouteWrapper>
          }
        /> */}
        <Route
          path="/floorplans/:id/add-vehicle"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <FloorplanAddVehicle />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/floorplans/:id/add-vendor"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <FloorplanAddVendor />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/floorplans/:id/vendors"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <FloorplanVendors />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/floorplans/:id/funding"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <FloorplansFunding />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/floorplans/:id/cancel-funding"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <FloorplansCancelFunding />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/floorplans/:id/active-vehicles"
          element={
            <DealerFlagRoute flags={['FloorplanON']}>
              <LazyRouteWrapper>
                <FloorplansActiveVehicles />
              </LazyRouteWrapper>
            </DealerFlagRoute>
          }
        />
        <Route
          path="/floorplans/:id/active-vehicles/detail/:floorplanId"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <FloorplansActiveVehiclesDetail />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/floorplans/:id/billing"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <FloorplanBilling />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/floorplans/:id/payoff-requests"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <FloorplansPayoffRequests />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/floorplans/:id/bulk-payoff"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <FloorplansBulkPayoff />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/floorplans/:id/paid-off-vehicles"
          element={
            <DealerFlagRoute flags={['FloorplanON']}>
              <LazyRouteWrapper>
                <FloorplansPaidOffVehicles />
              </LazyRouteWrapper>
            </DealerFlagRoute>
          }
        />
        <Route
          path="/floorplans/:id/paid-off-vehicles/detail/:floorplanId"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <FloorplansActiveVehiclesDetail />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/floorplans/:id/funding-log"
          element={
            <DealerFlagRoute flags={['FloorplanON']}>
              <LazyRouteWrapper>
                <FloorplansFundingLog />
              </LazyRouteWrapper>
            </DealerFlagRoute>
          }
        />
        <Route
          path="/floorplans/:id/funding-log/detail/:fundRecId"
          element={
            <DealerFlagRoute flags={['FloorplanON']}>
              <LazyRouteWrapper>
                <FloorplansFundingLogDetail />
              </LazyRouteWrapper>
            </DealerFlagRoute>
          }
        />
        <Route
          path="/floorplans/:id/billing-payment-log"
          element={
            <DealerFlagRoute flags={['FloorplanON']}>
              <LazyRouteWrapper>
                <FloorplansBillingPaymentLog />
              </LazyRouteWrapper>
            </DealerFlagRoute>
          }
        />
        <Route
          path="/floorplans/:id/billing-payment-log/detail/:billingLogId"
          element={
            <DealerFlagRoute flags={['FloorplanON']}>
              <LazyRouteWrapper>
                <FloorplansBillingPaymentLogDetail />
              </LazyRouteWrapper>
            </DealerFlagRoute>
          }
        />
        <Route
          path="/floorplans/:id/assign-lender"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <FloorplansAssignLender />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/floorplans/:id/balance-history"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <FloorplansBalanceHistory />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />

        <Route path="/home" element={<Navigate to="/home/news-info" />} />
        <Route
          path="/home/borrowing-base-reports"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <HomeBorrowingBaseReports />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/home/bulk-loanplan-billing"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <HomeBulkLoanPlanBilling />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/home/dashboards"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <HomeDashboards />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/home/docusign-status"
          element={
            <LazyRouteWrapper>
              <HomeDocuSignStatus />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/home/exports"
          element={
            <LazyRouteWrapper>
              <HomeExports />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/home/exports/:RecId"
          element={
            <LazyRouteWrapper>
              <HomeExportsDetail />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/home/lease-quote"
          element={
            <LazyRouteWrapper>
              <HomeLeaseQuote />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/home/news-info"
          element={
            <ProtectedRoute>
              <LazyRouteWrapper>
                <HomeNewsInfo />
              </LazyRouteWrapper>
            </ProtectedRoute>
          }
        />
        <Route
          path="/home/news-info/add-new"
          element={
            <LazyRouteWrapper>
              <HomeNewsInfoAddNew />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/home/prime-rate-history"
          element={
            <LazyRouteWrapper>
              <HomePrimeRateHistory />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/home/vin-search"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <HomeVinSearch />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        {/* Phone Directory disabled v0.63.8 — phoneext table is empty.
        <Route
          path="/home/directory"
          element={
            <LazyRouteWrapper>
              <HomeDirectory />
            </LazyRouteWrapper>
          }
        />
        */}
        <Route
          path="/home/calendar"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <HomeCalendar />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />

        <Route
          path="/imports"
          element={
            <LazyRouteWrapper>
              <ImportsSummary />
            </LazyRouteWrapper>
          }
        />
        <Route path="/imports/dashboard" element={<Navigate to="/imports" />} />
        <Route
          path="/imports/:id/check-for-duplicates"
          element={
            <LazyRouteWrapper>
              <ImportsCheckForDuplicates />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/imports/:id/current-dealer-ar"
          element={
            <LazyRouteWrapper>
              <ImportsCurrentDealerAR />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/imports/:id/ar-aged"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <ImportsARAged />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/imports/:id/ar-balance"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <ImportsARBalance />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/imports/:id/acc-depreciation"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <ImportsAccDepreciation />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/imports/:id/bhph-loan-requests"
          element={
            <DealerFlagRoute flags={['BHPHOn']}>
              <LazyRouteWrapper>
                <ImportsBHPHLoanRequests />
              </LazyRouteWrapper>
            </DealerFlagRoute>
          }
        />
        <Route
          path="/imports/:id/interest-balance"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <ImportsInterestBalance />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/imports/:id/floorplan-request"
          element={
            <DealerFlagRoute flags={['FloorplanON']}>
              <LazyRouteWrapper>
                <ImportsFloorplanRequest />
              </LazyRouteWrapper>
            </DealerFlagRoute>
          }
        />
        <Route
          path="/imports/:id/indirect-loan-requests"
          element={
            <DealerFlagRoute flags={['IndirectOn']}>
              <LazyRouteWrapper>
                <ImportsIndirectLoanRequests />
              </LazyRouteWrapper>
            </DealerFlagRoute>
          }
        />
        <Route
          path="/imports/:id/loan-requests"
          element={
            <DealerFlagRoute flags={['CreditLineON']}>
              <LazyRouteWrapper>
                <ImportsLoanRequests />
              </LazyRouteWrapper>
            </DealerFlagRoute>
          }
        />
        <Route
          path="/imports/:id/premier-loan-requests"
          element={
            <DealerFlagRoute flags={['HybridOn']}>
              <LazyRouteWrapper>
                <ImportsPremierLoanRequests />
              </LazyRouteWrapper>
            </DealerFlagRoute>
          }
        />
        <Route
          path="/imports/:id/refi-swap-premier-conversion"
          element={
            <DealerFlagRoute flags={['CreditLineON', 'BHPHOn', 'IndirectOn']}>
              <LazyRouteWrapper>
                <ImportsRefiSwapPremierConversion />
              </LazyRouteWrapper>
            </DealerFlagRoute>
          }
        />
        {/* lhphcapital-42bw: the dealer Repos/Swaps import IS the refi/swap import. Legacy's dealer
            Imports_N_M_RepoSwap is identical to the lender's, so one screen serves both. This
            path was a placeholder; it now forwards any old link to the real screen. */}
        <Route
          path="/imports/:id/repo-swaps"
          element={<Navigate to="../refi-swap-premier-conversion" relative="path" replace />}
        />
        <Route
          path="/imports/:id/unsold-inventory"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <ImportsUnsoldInventory />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/imports/:id/vin-to-stock-xref"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <ImportsVinToStockNumXref />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/imports/:id/wholesale-inventory"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <ImportsWholesaleInventory />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />

        <Route
          path="/loans"
          element={
            <LazyRouteWrapper>
              <LoansHome />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/billing"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <LoansBilling />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/loans/:id/active-loans"
          element={
            <LazyRouteWrapper>
              <LoansActiveLoans />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/active-loans/detail/:loanId"
          element={
            <LazyRouteWrapper>
              <LoansActiveLoansDetail />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/active-loans/detail/:loanId/repo-swap"
          element={
            <LazyRouteWrapper>
              <LoansRepoSwap />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/billing-payment-log"
          element={
            <LazyRouteWrapper>
              <LoansBillingPaymentLog />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/billing-payment-log/detail/:billingLogId"
          element={
            <LazyRouteWrapper>
              <LoansBillingPaymentLogDetail />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/bulk-payoff-requests"
          element={
            <LazyRouteWrapper>
              <LoansBulkPayoffRequests />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/close-full-term-loans"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <LoansCloseFullTermLoans />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/loans/:id/dashboard"
          element={
            <LazyRouteWrapper>
              <LoanDashboard />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/docusign-status"
          element={
            <LazyRouteWrapper>
              <LoansDocuSignStatus />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/exceptions-maintenance"
          element={
            <LazyRouteWrapper>
              <LoansExceptionsMaintenance />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/funding-log"
          element={
            <LazyRouteWrapper>
              <LoansFundingLog />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/funding-log/detail/:fundingLogId"
          element={
            <LazyRouteWrapper>
              <LoansFundingLogDetail />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/loan-calculator"
          element={
            <LazyRouteWrapper>
              <LoansLoanCalculator />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/loan-plan-payoff-requests"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <LoansLoanPlanPayoffRequests />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/loans/:id/loan-plan-bulk-payoff"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <LoansLoanPlanBulkPayoff />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/loans/:id/loan-to-lease-matchup"
          element={
            <LazyRouteWrapper>
              <LoanToLeaseMatchup />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/matchup-exceptions"
          element={
            <LazyRouteWrapper>
              <MatchupExceptions />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/matchup-exceptions/detail/:reportDate"
          element={
            <LazyRouteWrapper>
              <MatchupExceptionDetail />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/loan-match-status"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <LoansLoanMatchStatus />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/loans/:id/move-loans-to-floorplan"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <LoansMoveLoansToFloorplan />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/loans/:id/paid-off-loans"
          element={
            <LazyRouteWrapper>
              <LoansPaidOffLoans />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/paid-off-loans/detail/:loanId"
          element={
            <LazyRouteWrapper>
              <LoansActiveLoansDetail />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/payoff-calculator"
          element={
            <LazyRouteWrapper>
              <LoansPaidOffCalculator />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/payoff-requests-list"
          element={
            <LazyRouteWrapper>
              <LoansPayoffRequestsList />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/pending-funding"
          element={
            <LazyRouteWrapper>
              <LoansPendingFunding />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/pending-queue"
          element={
            <LazyRouteWrapper>
              <LoansPendingQueue />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/pending-repo-swaps"
          element={
            <LazyRouteWrapper>
              <LoansPendingRepoSwaps />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/repo-swap-approval"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <LoansRepoSwapApproval />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/loans/:id/cancel-swap-requests"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <LoansCancelSwapRequest />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/loans/:id/tranche-exceptions"
          element={
            <LazyRouteWrapper>
              <LoansTrancheExceptions />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/add-loan"
          element={
            <LazyRouteWrapper>
              <LoansAddLoan />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/loans/:id/fund-loans"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <LoansFundLoans />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/loans/:id/cancel-loan-requests"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <LoansCancelLoanRequests />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />

        {/* lhphcapital-3xb8: dealers get legacy's dealer report list; the staff pages are staff-only. */}
        <Route path="/reports" element={<ReportsIndexRedirect />} />
        <Route
          path="/reports/dealer-reports"
          element={
            <LazyRouteWrapper>
              <DealerReports />
            </LazyRouteWrapper>
          }
        />
        <Route
          path="/reports/credit-lines"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <ReportsCreditLines />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/reports/email-reports"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <ReportsEmailReports />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/reports/floor-plans"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <ReportsFloorplans />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />
        <Route
          path="/reports/all-reports"
          element={
            <StaffOnlyRoute>
              <LazyRouteWrapper>
                <ReportsAllReports />
              </LazyRouteWrapper>
            </StaffOnlyRoute>
          }
        />

        <Route
          path="/user/profile"
          element={
            <LazyRouteWrapper>
              <UserProfile />
            </LazyRouteWrapper>
          }
        />

        <Route
          path="/video-training"
          element={
            <LazyRouteWrapper>
              <VideoTraining />
            </LazyRouteWrapper>
          }
        />

        <Route
          path="/unauthorized"
          element={
            <LazyRouteWrapper>
              <UnauthorizedPage />
            </LazyRouteWrapper>
          }
        />

        <Route path="*" element={<Navigate to="/home/news-info" />} />

        {/* Catch all for protected routes */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
};

export default AppRoutes;
